import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, Button, Icon, Label, Segment } from 'semantic-ui-react';
import { API, showError, showSuccess } from '../../../helpers';
import BillingConfigModal from './BillingConfigModal';
import AlibabaBillingImportModal from './AlibabaBillingImportModal';

const PROVIDER_NAMES = {
  15: '百度千帆',
  17: '阿里',
  40: '字节豆包',
  47: '百度千帆V2',
};

const BillingConfigList = () => {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await API.get('/api/admin/channel-billing/configs');
      if (res.data.success) {
        setConfigs(res.data.data || []);
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError(t('channel_billing.config.load_error', '加载配置失败'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        t(
          'channel_billing.config.confirm_delete',
          '确定删除此配置？关联的账单记录也将被删除。'
        )
      )
    ) {
      return;
    }
    try {
      const res = await API.delete(`/api/admin/channel-billing/configs/${id}`);
      if (res.data.success) {
        showSuccess(t('channel_billing.config.deleted', '删除成功'));
        fetchConfigs();
      } else {
        showError(res.data.message);
      }
    } catch (error) {
      showError(t('channel_billing.config.delete_error', '删除失败'));
    }
  };

  const formatTime = (ts) => {
    if (!ts) return '-';
    return new Date(ts * 1000).toLocaleString();
  };

  return (
    <>
      <Segment>
        <Button
          primary
          onClick={() => {
            setEditingConfig(null);
            setModalOpen(true);
          }}
        >
          <Icon name='plus' />
          {t('channel_billing.config.add', '新增配置')}
        </Button>
        <Button color='teal' onClick={() => setImportModalOpen(true)}>
          <Icon name='upload' />
          导入阿里账单
        </Button>
      </Segment>

      <Table celled loading={loading ? 'true' : undefined}>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>ID</Table.HeaderCell>
            <Table.HeaderCell>
              {t('channel_billing.config.name', '名称')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('channel_billing.config.provider', '提供商')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('channel_billing.config.status', '状态')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('channel_billing.config.last_sync', '上次同步')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('channel_billing.config.error', '错误')}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {t('channel_billing.config.actions', '操作')}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {configs.map((config) => (
            <Table.Row key={config.id}>
              <Table.Cell>{config.id}</Table.Cell>
              <Table.Cell>{config.name}</Table.Cell>
              <Table.Cell>
                {PROVIDER_NAMES[config.provider_type] || config.provider_type}
              </Table.Cell>
              <Table.Cell>
                <Label color={config.enabled ? 'green' : 'grey'} size='small'>
                  {config.enabled
                    ? t('channel_billing.config.enabled', '启用')
                    : t('channel_billing.config.disabled', '禁用')}
                </Label>
              </Table.Cell>
              <Table.Cell>{formatTime(config.last_sync_time)}</Table.Cell>
              <Table.Cell
                style={{ color: 'red', maxWidth: 200, wordBreak: 'break-all' }}
              >
                {config.last_sync_error || '-'}
              </Table.Cell>
              <Table.Cell>
                <Button
                  size='small'
                  onClick={() => {
                    setEditingConfig(config);
                    setModalOpen(true);
                  }}
                >
                  {t('channel_billing.config.edit', '编辑')}
                </Button>
                <Button
                  size='small'
                  color='red'
                  onClick={() => handleDelete(config.id)}
                >
                  {t('channel_billing.config.delete', '删除')}
                </Button>
              </Table.Cell>
            </Table.Row>
          ))}
          {configs.length === 0 && (
            <Table.Row>
              <Table.Cell colSpan={7} textAlign='center'>
                {t(
                  'channel_billing.config.no_data',
                  '暂无配置，请点击"新增配置"添加'
                )}
              </Table.Cell>
            </Table.Row>
          )}
        </Table.Body>
      </Table>

      <BillingConfigModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        config={editingConfig}
        onSaved={fetchConfigs}
      />
      <AlibabaBillingImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImported={fetchConfigs}
      />
    </>
  );
};

export default BillingConfigList;
